import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { stageColor, stageLabel } from '../lib/stages';
import GettingStartedChecklist from '../components/GettingStartedChecklist';

// ─── Styles ────────────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: 'white',
  borderRadius: 10,
  padding: 20,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  border: '1px solid #e5e7eb',
};
const metricCard: React.CSSProperties = {
  ...card,
  textAlign: 'center',
  padding: '24px 16px',
};
const sectionTitle: React.CSSProperties = { fontSize: 15, fontWeight: 600, marginBottom: 14, color: '#1f2937' };
const grid4: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 };
const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 20, marginBottom: 28 };
const listItem: React.CSSProperties = { padding: '12px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13, transition: 'background 0.15s' };
const emptyMsg: React.CSSProperties = { color: '#9ca3af', fontSize: 13, fontStyle: 'italic', padding: '12px 0' };
const viewAllLink: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: '#2563eb', textDecoration: 'none', marginTop: 12, display: 'inline-block' };
const stageBadge = (stage: string): React.CSSProperties => {
  const c = stageColor(stage);
  return { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color };
};
const statusBadge = (status: string): React.CSSProperties => {
  if (status === 'in_progress') return { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1e40af' };
  return { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#fef3c7', color: '#92400e' };
};
// ─── Types ─────────────────────────────────────────────────────────────────────
interface DashboardState {
  activeCases: number;
  pendingTasks: number;
  awaitingApproval: number;
  activeAlerts: number;
  tasks: any[];
  opportunities: any[];
  cases: any[];
  alerts: any[];
  vendorMessages: any[];
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const [casesRes, tasksRes, approvalsRes, alertsRes, oppsRes, messagesRes] = await Promise.all([
          api.get<any>('/procurement-cases').catch(() => ({ items: [], total: 0 })),
          api.get<any>('/tasks').catch(() => ({ items: [], total: 0 })),
          api.get<any>('/approvals?status=pending').catch(() => ({ items: [], total: 0 })),
          api.get<any>('/alerts?status=active').catch(() => ({ items: [], total: 0 })),
          api.get<any>('/opportunities?page_size=50').catch(() => ({ items: [], total: 0 })),
          api.get<any>('/messages').catch(() => ({ items: [], total: 0 })),
        ]);

        const cases = casesRes?.items || [];
        const tasks = tasksRes?.items || [];
        const approvals = approvalsRes?.items || [];
        const alerts = alertsRes?.items || [];
        const opportunities = oppsRes?.items || [];
        const messages = messagesRes?.items || [];

        // Filter active tasks (not_started + in_progress)
        const pendingTasks = tasks.filter(
          (t: any) => t.status === 'not_started' || t.status === 'in_progress'
        );

        // Sort tasks by due_date
        const sortedTasks = [...pendingTasks].sort((a: any, b: any) => {
          if (!a.due_date) return 1;
          if (!b.due_date) return -1;
          return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
        });

        // Sort opportunities by response_deadline within next 14 days
        const now = new Date();
        const in14Days = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
        const upcomingOpps = opportunities
          .filter((o: any) => {
            if (!o.response_deadline) return false;
            const deadline = new Date(o.response_deadline);
            return deadline >= now && deadline <= in14Days;
          })
          .sort((a: any, b: any) => new Date(a.response_deadline).getTime() - new Date(b.response_deadline).getTime());

        // Sort cases by updated_at desc
        // Normalize timestamps that may be Unix seconds, ms, or ISO strings so
        // the sort is correct even when records mix formats.
        const tsMs = (v: any): number => {
          if (v == null || v === '') return 0;
          if (typeof v === 'number' || /^\d+$/.test(String(v))) {
            let n = Number(v);
            if (n < 1e12) n *= 1000;
            return n;
          }
          const t = new Date(v).getTime();
          return isNaN(t) ? 0 : t;
        };
        const sortedCases = [...cases].sort((a: any, b: any) =>
          tsMs(b.updated_at || b.created_at) - tsMs(a.updated_at || a.created_at));

        // Recent active alerts (newest first)
        const sortedAlerts = [...alerts].sort((a: any, b: any) =>
          tsMs(b.triggered_at || b.created_at) - tsMs(a.triggered_at || a.created_at));

        // Last communication per vendor: newest message for each vendor
        const sortedMessages = [...messages].sort((a: any, b: any) =>
          tsMs(b.sent_at || b.created_at) - tsMs(a.sent_at || a.created_at));
        const seenVendors = new Set<string>();
        const lastByVendor = sortedMessages.filter((m: any) => {
          const key = (m.vendor_name || m.vendor_id || m.recipient_email || '').toLowerCase();
          if (!key || seenVendors.has(key)) return false;
          seenVendors.add(key);
          return true;
        });

        setData({
          activeCases: casesRes?.total || cases.length,
          pendingTasks: pendingTasks.length,
          awaitingApproval: approvalsRes?.total || approvals.length,
          activeAlerts: alertsRes?.total || alerts.length,
          tasks: sortedTasks.slice(0, 5),
          opportunities: upcomingOpps.slice(0, 5),
          cases: sortedCases.slice(0, 5),
          alerts: sortedAlerts.slice(0, 5),
          vendorMessages: lastByVendor.slice(0, 5),
        });
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    }

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div role="status" aria-live="polite" style={{ color: '#6b7280', padding: 32 }}>
        Loading dashboard...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div role="alert" style={{ color: '#ef4444', padding: 32 }}>
        {error || 'Failed to load dashboard data. Please try refreshing.'}
      </div>
    );
  }

  const now = new Date();

  // Robustly parse a timestamp that may be Unix SECONDS (our DynamoDB records,
  // e.g. 1788280000), milliseconds, or an ISO date string. Returns a Date or null.
  const parseTs = (v: any): Date | null => {
    if (v == null || v === '') return null;
    if (typeof v === 'number' || /^\d+$/.test(String(v))) {
      let n = Number(v);
      if (n < 1e12) n *= 1000; // seconds -> ms (values below ~2001 in ms are really seconds)
      const d = new Date(n);
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };
  const fmtDate = (v: any): string => {
    const d = parseTs(v);
    return d ? d.toLocaleDateString() : '';
  };

  const dueDateStyle = (dateStr: string): React.CSSProperties => {
    const date = new Date(dateStr);
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { color: '#dc2626', fontWeight: 600, fontSize: 12 };
    if (diffDays <= 3) return { color: '#d97706', fontWeight: 600, fontSize: 12 };
    return { color: '#6b7280', fontSize: 12 };
  };

  return (
    <div>
      <h2 style={{ marginBottom: 24, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Procurement Dashboard</h2>
      <GettingStartedChecklist />

      {/* ═══ ROW 1: KEY METRICS ═══ */}
      <div style={grid4} role="region" aria-label="Key metrics">
        <div style={metricCard}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#2563eb' }}>{data.activeCases}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Active Cases</div>
        </div>
        <div style={metricCard}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b' }}>{data.pendingTasks}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Pending Tasks</div>
        </div>
        <div style={metricCard}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#7c3aed' }}>{data.awaitingApproval}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Awaiting Approval</div>
        </div>
        <div style={metricCard}>
          <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{data.activeAlerts}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>Active Alerts</div>
        </div>
      </div>

      {/* ═══ ROW 2: TASKS + DEADLINES ═══ */}
      <div style={grid2}>
        {/* My Tasks Due Soon */}
        <div style={card}>
          <h3 style={sectionTitle}>My Tasks Due Soon</h3>
          {data.tasks.length === 0 ? (
            <p style={emptyMsg}>No pending tasks. You're all caught up.</p>
          ) : (
            <div>
              {data.tasks.map((task: any, idx: number) => (
                <div key={task.id || idx} style={listItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, color: '#1f2937', marginBottom: 4 }}>
                        {task.title || 'Untitled task'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {task.due_date && (
                          <span style={dueDateStyle(task.due_date)}>
                            Due: {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        )}
                        <span style={statusBadge(task.status)}>{task.status?.replace('_', ' ')}</span>
                        {task.case_id && (
                          <Link to={`/tasks`} style={{ fontSize: 11, color: '#2563eb' }}>
                            {task.case_id}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/tasks" style={viewAllLink}>View All Tasks &rarr;</Link>
            </div>
          )}
        </div>

        {/* Approaching Deadlines */}
        <div style={card}>
          <h3 style={sectionTitle}>Approaching Deadlines</h3>
          {data.opportunities.length === 0 ? (
            <p style={emptyMsg}>No deadlines in the next 14 days.</p>
          ) : (
            <div>
              {data.opportunities.map((opp: any, idx: number) => (
                <Link
                  key={opp.id || idx}
                  to={`/opportunities/${opp.id}`}
                  style={{ ...listItem, display: 'block', textDecoration: 'none', cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                  onMouseLeave={e => (e.currentTarget.style.background = '')}
                >
                  <div style={{ fontWeight: 500, color: '#1f2937', marginBottom: 4 }}>
                    {opp.title?.substring(0, 60) || 'Untitled opportunity'}
                    {opp.title?.length > 60 ? '...' : ''}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={dueDateStyle(opp.response_deadline)}>
                      {new Date(opp.response_deadline).toLocaleDateString()}
                    </span>
                    {opp.agency && (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{opp.agency}</span>
                    )}
                  </div>
                </Link>
              ))}
              <Link to="/opportunities" style={viewAllLink}>View All Opportunities &rarr;</Link>
            </div>
          )}
        </div>
      </div>

      {/* ═══ ROW 3: RECENT PROCUREMENT CASES ═══ */}
      <div style={{ marginBottom: 28 }}>
        <div style={card}>
          <h3 style={sectionTitle}>Recent Procurement Cases</h3>
          {data.cases.length === 0 ? (
            <p style={emptyMsg}>No procurement cases yet.</p>
          ) : (
            <div>
              {data.cases.map((c: any, idx: number) => {
                const linkedOppId = (Array.isArray(c.opportunity_ids) && c.opportunity_ids[0]) || c.opportunity_id || '';
                const rowInner = (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#2563eb', fontSize: 12, marginRight: 10 }}>
                        {c.case_id || c.case_number || c.id?.slice(0, 8)}
                      </span>
                      <span style={{ fontWeight: 500, color: '#1f2937' }}>
                        {c.title?.substring(0, 55) || 'Untitled case'}
                        {c.title?.length > 55 ? '...' : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {c.current_stage && <span style={stageBadge(c.current_stage)}>{stageLabel(c.current_stage)}</span>}
                      {(c.updated_at || c.created_at) && (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          {fmtDate(c.updated_at || c.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                );
                return linkedOppId ? (
                  <Link
                    key={c.case_id || c.id || idx}
                    to={`/opportunities/${linkedOppId}`}
                    style={{ ...listItem, display: 'block', textDecoration: 'none', cursor: 'pointer' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    {rowInner}
                  </Link>
                ) : (
                  <div key={c.case_id || c.id || idx} style={listItem}>{rowInner}</div>
                );
              })}
              <Link to="/cases" style={viewAllLink}>View All Cases &rarr;</Link>
            </div>
          )}
        </div>
      </div>

      {/* ═══ ROW 4: ALERTS + LAST VENDOR COMMUNICATION ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
        {/* Alerts */}
        <div style={card}>
          <h3 style={sectionTitle}>Alerts</h3>
          {(!data.alerts || data.alerts.length === 0) ? (
            <p style={emptyMsg}>No active alerts.</p>
          ) : (
            <div>
              {data.alerts.map((a: any, idx: number) => (
                <div key={a.id || idx} style={listItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: '#1f2937', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.title || a.rule_name || 'Alert'}
                      </div>
                      {(a.rule_name || a.description) && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.rule_name ? `Rule: ${a.rule_name}` : String(a.description).slice(0, 70)}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {a.match_type && <span style={statusBadge(a.match_type)}>{a.match_type}</span>}
                      {(a.triggered_at || a.created_at) && (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(a.triggered_at || a.created_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/alerts" style={viewAllLink}>View All Alerts &rarr;</Link>
            </div>
          )}
        </div>

        {/* Last Communication with Vendors */}
        <div style={card}>
          <h3 style={sectionTitle}>Last Communication with Vendors</h3>
          {(!data.vendorMessages || data.vendorMessages.length === 0) ? (
            <p style={emptyMsg}>No vendor communications yet.</p>
          ) : (
            <div>
              {data.vendorMessages.map((m: any, idx: number) => (
                <div key={m.id || idx} style={listItem}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {m.vendor_name || m.recipient_email || 'Vendor'}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {(m.subject || m.message_type || 'Message')}{m.channel ? ` · ${m.channel}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {m.status && <span style={statusBadge(m.status)}>{m.status}</span>}
                      {(m.sent_at || m.created_at) && (
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(m.sent_at || m.created_at)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/engagement" style={viewAllLink}>View All Communications &rarr;</Link>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
